// src/components/auth/RequireRole.jsx
import React, { useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const ROLE_BASE = {
  superadmin: "/admin",
  schooladmin: "/school",
  teacher: "/teacher",
  student: "/student",
};

export default function RequireRole({ role = "schooladmin", children }) {
  // Support both old ({ loading }) and new ({ authReady, profileReady }) contexts
  const {
    user,
    profile,
    loading: authLoading,      // older field name
    authReady,                 // newer field (boolean)
    profileReady,              // newer field (boolean)
  } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();

  // Normalize readiness flags
  const isAuthReady = typeof authReady === "boolean" ? authReady : !authLoading;
  const isProfileReady = typeof profileReady === "boolean" ? profileReady : profile !== undefined;

  // One-time “lastPath” restore after refresh (optional)
  const restoredRef = useRef(false);
  const myRole = String(profile?.role || "").toLowerCase();
  useEffect(() => {
    if (restoredRef.current) return;
    if (!isAuthReady || !isProfileReady || !user || !myRole) return;

    const base = ROLE_BASE[myRole];
    if (!base) {
      restoredRef.current = true;
      return;
    }

    const saved = localStorage.getItem("lastPath") || "";
    // Restore only if the saved path belongs to this role’s prefix
    if (saved && saved.startsWith(base)) {
      const current = location.pathname + location.search;
      if (current !== saved) {
        restoredRef.current = true;
        navigate(saved, { replace: true });
        return;
      }
    }

    restoredRef.current = true;
  }, [isAuthReady, isProfileReady, user, myRole, navigate, location.pathname, location.search]);

  // While Firebase auth/profile are resolving, show a tiny placeholder (prevents blank screen)
  if (!isAuthReady || !isProfileReady) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  // Not signed in → go to login with next=
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  // Role check
  const requiredRole = String(role).toLowerCase();
  if (!myRole) {
    // Profile exists but has no role → block (send to login)
    return <Navigate to="/login" replace />;
  }

  if (myRole !== requiredRole) {
    // Send them to their base home (prevents loops)
    const base = ROLE_BASE[myRole] || "/login";
    return <Navigate to={base} replace />;
  }

  return children;
}
